import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast
} from '@chakra-ui/react';
import { MdPowerSettingsNew } from 'react-icons/md';
import { Icon } from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon, RepeatIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Client {
  _id: string;
  company: string;
  status: 'active' | 'inactive';
  joinDate: string;
  address?: string;
  pointOfContact?: {
    name: string;
    designation: string;
    contactNumber: string;
    email: string;
  };
}

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [viewingClient, setViewingClient] = useState<Client | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Fetch clients from backend
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  React.useEffect(() => {
    async function fetchClients() {
      setLoading(true);
      setError(null);
      try {
        const res = await import('../services/api').then(m => m.clientsAPI.getAll());
        setClients(res.data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch clients');
      } finally {
        setLoading(false);
      }
    }
    fetchClients();
  }, []);

  const handleAddClient = () => {
    navigate('/clients/add');
  };

  const handleEditClient = (client: Client) => {
    navigate(`/clients/edit/${client._id}`);
  };

  const handleDeleteClient = (id: string) => {
    import('../services/api').then(m => m.clientsAPI.delete(id)).then(() => {
      setClients(prev => prev.filter(c => c._id !== id));
    }).catch(err => {
      setError(err.message || 'Failed to delete client');
    });
  };

  const toast = useToast();

  const toggleStatus = async (client: Client) => {
    const id = client._id;
    const newStatus = client.status === 'active' ? 'inactive' : 'active';
    try {
      await import('../services/api').then(m => m.clientsAPI.update(id, { status: newStatus }));
      setClients(prev => prev.map(c => (c._id === id ? { ...c, status: newStatus } : c)));
      toast({
        title: 'Status updated',
        description: `${client.company} is now ${newStatus}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
      toast({
        title: 'Error',
        description: 'Failed to update client status. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'green' : 'gray';
  };

  const formatPhone = (phone: string | undefined) => {
    if (!phone || !phone.startsWith('+')) return phone || '';
    const countryCodes = ['+1', '+44', '+91', '+61', '+49', '+33', '+81'].sort((a, b) => b.length - a.length);
    for (const code of countryCodes) {
      if (phone.startsWith(code)) {
        return `${code}-${phone.slice(code.length)}`;
      }
    }
    return phone;
  };

  return (
    <Box>
      {/* Page Header */}
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>
          Clients
        </Heading>
        <Text color="gray.600">
          Onboard and manage your client relationships
        </Text>
      </Box>

      {/* Actions */}
      <Box mb={6}>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="primary"
          onClick={handleAddClient}
          className="btn-primary"
        >
          Onboard Client
        </Button>
      </Box>

      {/* Clients Table */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={0}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th className="table-header">Company Name</Th>
                <Th className="table-header">Join Date</Th>
                <Th className="table-header">Point of Contact</Th>
                <Th className="table-header text-center" textAlign="center">Status</Th>
                <Th className="table-header">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {clients.map((client) => (
                <Tr key={client._id}>
                  <Td className="table-cell">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">{client.company}</Text>
                      <Text fontSize="sm" color="gray.500">
                        ID:{client._id}
                      </Text>
                    </VStack>
                  </Td>
                  <Td className="table-cell">{client.joinDate}</Td>
                  <Td className="table-cell">
                    {client.pointOfContact ? (
                      <VStack align="start" spacing={0} fontSize="sm">
                        <Text><b>Name:</b> {client.pointOfContact.name}</Text>
                        <Text><b>Designation:</b> {client.pointOfContact.designation}</Text>
                        <Text><b>Contact:</b> {formatPhone(client.pointOfContact.contactNumber)}</Text>
                        <HStack spacing={1}>
                          <Text fontWeight="bold">Email:</Text>
                          <Menu>
                            <MenuButton as={Text} color="blue.500" textDecoration="underline" cursor="pointer">
                              {client.pointOfContact.email}
                            </MenuButton>
                            <MenuList>
                              <MenuItem as="a" href={`mailto:${client.pointOfContact.email}`}>Default Mail Client</MenuItem>
                              <MenuItem as="a" href={`https://mail.google.com/mail/?view=cm&fs=1&to=${client.pointOfContact.email}`} target="_blank">Gmail</MenuItem>
                              <MenuItem as="a" href={`https://outlook.live.com/mail/0/deeplink/compose?to=${client.pointOfContact.email}`} target="_blank">Outlook</MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </VStack>
                    ) : <Text color="gray.400">No contact info</Text>}
                  </Td>
                  <Td className="table-cell text-center" textAlign="center">
                    <Badge
                      colorScheme={getStatusColor(client.status)}
                      variant="subtle"
                      px={3}
                      py={1}
                      textTransform="capitalize"
                      fontSize="sm"
                      borderRadius="md"
                      minW="80px"
                      textAlign="center"
                      display="inline-block"
                    >
                      {client.status}
                    </Badge>
                  </Td>
                  <Td className="table-cell">
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="Toggle status"
                        icon={<RepeatIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleStatus(client)}
                        title={client.status === 'active' ? 'Deactivate' : 'Activate'}
                        sx={{ color: client.status === 'active' ? 'red' : 'green' }}
                      />
                      <IconButton
                        aria-label="View client"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => { setViewingClient(client); onOpen(); }}
                      />
                      <IconButton
                        aria-label="Edit client"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => handleEditClient(client)}
                      />
                      <IconButton
                        aria-label="Delete client"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDeleteClient(client._id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Modal for viewing client details */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Client Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingClient && (
              <VStack align="start" spacing={3}>
                <Text><b>Company Name:</b> {viewingClient.company}</Text>
                <Text><b>Status:</b> {viewingClient.status}</Text>
                <Text><b>Join Date:</b> {viewingClient.joinDate}</Text>
                {viewingClient.address && (
                  <Text><b>Address:</b> {viewingClient.address}</Text>
                )}
                {viewingClient.pointOfContact && (
                  <Box mt={2}>
                    <Text fontWeight="bold">Point of Contact:</Text>
                    <Text>Name: {viewingClient.pointOfContact.name}</Text>
                    <Text>Designation: {viewingClient.pointOfContact.designation}</Text>
                    <Text>Contact Number: {formatPhone(viewingClient.pointOfContact.contactNumber)}</Text>
                    <HStack spacing={1}>
                      <Text fontWeight="bold">Email:</Text>
                      <Menu>
                        <MenuButton as={Text} color="blue.500" textDecoration="underline" cursor="pointer">
                          {viewingClient.pointOfContact.email}
                        </MenuButton>
                        <MenuList>
                          <MenuItem as="a" href={`mailto:${viewingClient.pointOfContact.email}`}>Default Mail Client</MenuItem>
                          <MenuItem as="a" href={`https://mail.google.com/mail/?view=cm&fs=1&to=${viewingClient.pointOfContact.email}`} target="_blank">Gmail</MenuItem>
                          <MenuItem as="a" href={`https://outlook.live.com/mail/0/deeplink/compose?to=${viewingClient.pointOfContact.email}`} target="_blank">Outlook</MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Clients;
